import { jsx } from "react/jsx-runtime";
import { useContext, useMemo } from "react";
import { ThemeContext } from "styled-components";
import PropTypes from "prop-types";
import { useLastValidRefinanceValues, useRefinanceColors, abbr } from "./RefinanceCalculator.utils.esm.js";
import PropInterpolatorWrapper from "../PaymentDonutChart/Chart/common/PropInterpolator.esm.js";
import withDimensions from "../PaymentDonutChart/withDimensions.esm.js";
import { getWholeDollars } from "../../utils/zhl-calculator.esm.js";
import LineChart from "../PaymentDonutChart/LineChart/LineChart.esm.js";
const CHART_NAME = "Total Savings";
const monthFormatter = (num) => `${num} month${num > 1 ? "s" : ""}`;
const RefinanceLineGraphBase = ({
  containerWidth
}) => {
  const values = useLastValidRefinanceValues();
  const colors = useRefinanceColors();
  const theme = useContext(ThemeContext);
  const prepareData = function prepareData2() {
    const schedule = values.calculations.savingsSchedule;
    const dataValues = [];
    for (let i = 0; i < schedule.length; i += 1) {
      const months = monthFormatter(schedule[i].month);
      const dataValue = {
        x: i,
        y: schedule[i].totalSavings
      };
      if (schedule[i].breakEven) {
        dataValue.info = `This is your break even point. After ${months}, your total savings will be greater than any costs to refinance your loan.`;
        dataValue.color = colors.infoPointColor;
      }
      if (schedule[i].breakNegative) {
        dataValue.info = `After this point, you will no longer be saving money. Unless there is a break point after ${months}, then refinancing is not a good idea.`;
        dataValue.color = colors.infoPointColor;
      }
      if (schedule[i].currentLoanComplete && i !== schedule.length - 1) {
        dataValue.info = `After ${months} you will have paid off your previous loan and all additional payments to your new loan will decrease your total savings.`;
        dataValue.color = colors.infoPointColor;
      }
      if (schedule[i].newLoanComplete && i !== schedule.length - 1) {
        dataValue.info = `After ${months} you will have paid off your new loan. From this point forward you will continue to save money until your old loan is paid off.`;
        dataValue.color = colors.infoPointColor;
      }
      dataValues.push(dataValue);
    }
    return [{
      name: CHART_NAME,
      formatter: (value) => (
        // eslint-disable-next-line no-restricted-globals
        isNaN(value) || value === null ? null : getWholeDollars(value)
      ),
      values: dataValues
    }];
  };
  const lineData = useMemo(prepareData, [values.calculations.savingsSchedule, colors.infoPointColor]);
  const legendIndexFormatter = (x) => {
    if (x === null) {
      return null;
    }
    const isPlural = x > 1;
    const suffix = `month${isPlural ? "s" : ""}`;
    return `${x} ${suffix}`;
  };
  return /* @__PURE__ */ jsx("div", { style: {
    position: "relative",
    paddingTop: "60px"
  }, children: /* @__PURE__ */ jsx("div", { style: {
    fontSize: "10px",
    height: "245px"
  }, children: /* @__PURE__ */ jsx(LineChart, { containerWidth, legend: false, legendIndexFormatter, margins: {
    top: 10,
    right: 40,
    bottom: 40,
    left: 20
  }, data: lineData, colors: () => colors.lifetimeSavingsColor, xHideOrigin: true, xAxisTickCount: 3, xAxisFormatter: (x) => `${x} mo`, yAxisFormatter: (x) => `$${abbr(x)}`, yAxisTickCount: 4, lineWidth: 2, height: 245, axesColor: theme.constellation.colors.backgroundLight, tickTextStroke: theme.constellation.colors.textLight, displayDataPoints: false, xLabel: "When", yLabel: CHART_NAME }) }) });
};
RefinanceLineGraphBase.defaultProps = {};
RefinanceLineGraphBase.propTypes = {
  containerWidth: PropTypes.number.isRequired
};
const RefinanceLineGraph = PropInterpolatorWrapper(withDimensions(RefinanceLineGraphBase, {
  defaultWidth: 980
}));
export {
  RefinanceLineGraph as default
};
